import {
  ArrowDropDown,
  Close,
  Create,
  HelpOutline,
  Reviews,
  WidthWide
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import ChatList from './ChatList';
import { useAppSelector } from 'src/app/hooks';
import { positionMode } from '..';
import { useState } from 'react';
import EmptyChat from './EmptyChat';
import ChatInput from './ChatInput';
import { TChatMode } from 'src/features/chatNow';
import { AIAssistantPositionType } from 'src/features/AIAssistantPosition';
import { CHAT_NOW_GUIDELINES } from 'src/route';
import { useNavigate } from 'react-router';

export default function AIAssistantSideView({
  customWidthPercentage,
  chatInputValue,
  handleChange,
  handleChangePositionMode,
  handleClickTopic,
  handleCloseAIAssistant,
  handleSendBooleanQueryMessage,
  handleSendChatNowMessage,
  handleSendMessage,
  isLoading,
  sendingLoading,
  handleClickNewChat
}: {
  customWidthPercentage: string;
  handleClickTopic: (topic: TChatMode) => void;
  handleSendBooleanQueryMessage: (content: string) => Promise<void>;
  handleSendChatNowMessage: (content: string) => Promise<void>;
  chatInputValue: string;
  isLoading: boolean;
  sendingLoading: boolean;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSendMessage: (content: string) => Promise<void>;
  handleCloseAIAssistant: () => void;
  handleChangePositionMode: (mode: AIAssistantPositionType) => void;
  handleClickNewChat: () => Promise<void>;
}) {
  const theme = useTheme();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleOpenMode = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMode = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const {
    conversation: chatConversation,
    chatMode,
    booleanConversation
  } = useAppSelector((state) => state.chatNow);

  const { position: positionAIAssistant } = useAppSelector(
    (state) => state.storeAIAssistantPosition
  );
  const conversation =
    chatMode === 'chatNow' ? chatConversation : booleanConversation;

  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const mobileStyle: SxProps<Theme> = {
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 6
  };

  const isTelkomsel =
    window.location.hostname.includes('tsight') ||
    window.location.hostname.includes('sentrix');

  return (
    <>
      <Box
        width={customWidthPercentage}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={4}
        sx={{
          height: '100%',
          backgroundColor: theme.palette.grey[50],
          ...(matchesUpMd ? {} : mobileStyle)
        }}
      >
        <Box
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            padding: 2,
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            pointerEvents: isLoading || sendingLoading ? 'none' : 'auto'
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Reviews htmlColor={theme.colors.primary.main} />
            <Typography>AI Assistant</Typography>
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            <Tooltip title="New Chat">
              <IconButton
                size="small"
                sx={{ width: 30, height: 30 }}
                onClick={handleClickNewChat}
              >
                <Create fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isTelkomsel ? (
              <Tooltip title="Open Guideline">
                <IconButton
                  size="small"
                  onClick={() => navigate(CHAT_NOW_GUIDELINES)}
                  sx={{ width: 30, height: 30 }}
                >
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {matchesUpMd ? (
              <Tooltip title="Open View Options">
                <Box
                  sx={{
                    cursor: 'pointer',
                    ':hover': {
                      opacity: 0.5
                    },
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    border: 0,
                    backgroundColor: 'transparent'
                  }}
                  onClick={(event) => handleOpenMode(event)}
                  component="button"
                >
                  <WidthWide fontSize="small" />
                  <ArrowDropDown fontSize="small" />
                </Box>
              </Tooltip>
            ) : null}
            <Tooltip title="Close AI Assistant">
              <IconButton
                size="small"
                onClick={handleCloseAIAssistant}
                sx={{ width: 30, height: 30 }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        {conversation.length === 0 ? (
          <EmptyChat
            handleClickTopic={handleClickTopic}
            handleSendBooleanQueryMessage={handleSendBooleanQueryMessage}
            handleSendChatNowMessage={handleSendChatNowMessage}
            chatInputValue={chatInputValue}
            handleChange={handleChange}
            handleSendMessage={handleSendMessage}
            isLoading={isLoading}
            sendingLoading={sendingLoading}
          />
        ) : (
          <Stack justifyContent="space-between" height="100%">
            <ChatList
              conversation={conversation}
              sendingLoading={sendingLoading}
            />
            <ChatInput
              chatInputValue={chatInputValue}
              handleChange={handleChange}
              handleSendMessage={handleSendMessage}
              isLoading={isLoading}
              sendingLoading={sendingLoading}
            />
          </Stack>
        )}

        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseMode}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
        >
          <List>
            {positionMode.map((mode) => (
              <ListItem
                disablePadding
                key={mode.value}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <ListItemButton
                  onClick={() => {
                    handleChangePositionMode(mode.value);
                    setAnchorEl(null);
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light
                    },
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    gap: 1,
                    backgroundColor:
                      positionAIAssistant === mode.value
                        ? theme.palette.primary.light
                        : ''
                  }}
                >
                  <Typography sx={{ fontSize: '12px' }}>
                    {mode.label}
                  </Typography>
                  {mode.icon}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Popover>
      </Box>
    </>
  );
}
